import React, { ReactNode } from 'react';
import { StyleSheet, TouchableOpacity } from 'react-native';

import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { EventInfo, EventInfoWithoutPlayers } from '@/api/events/types/types';
import { ImageWithGradient } from '@/components/ImageWithGradient';
import { LiveTag } from '@/components/LiveTag';
import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { LinearGradient } from '@/components/linear-gradient/LinearGradient';
import { useEventsTabsStore } from '@/hooks/use-events-tabs-store';
import { RootStackParamList } from '@/navigation/types';
import { designSystem, withOpacity } from '@/styles/styles';
import { EventAttributesType, EventStatus } from '@/types/api.generated';
import { findEventAttribute } from '@/utils/fantasy-attribute-utils';
import { gameUtils } from '@/utils/games';

export const WIDTH = 240;
export const HEIGHT = 146;

export const DedicatedEventCard = ({ event, index }: { event: EventInfo | EventInfoWithoutPlayers; index: number }) => {
    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

    const image = event.headerImage;
    const title = gameUtils.createHeaderTitleLabel(event);
    const info = gameUtils.createInfoLabel(event);
    const date = gameUtils.createTimeLabel(event);
    const startDate = findEventAttribute(event, EventAttributesType.TournamentStartDateTime) ?? '';
    const endDate = findEventAttribute(event, EventAttributesType.TournamentEndDateTime) ?? '';
    const dateRanges = gameUtils.formatTournamentDates(startDate, endDate);
    const isLive = event.status === EventStatus.InProgress;
    const navigateToEventDetailSheet = () => {
        useEventsTabsStore.getState().actions.setSelectedTab(index);
        navigation.navigate('FantasyEventsSheets', {
            league: event.league,
            eventId: event.id || '',
            contextKey: new Date().getTime().toString(),
        });
    };
    return (
        <TouchableOpacity onPress={navigateToEventDetailSheet}>
            <Box
                width={WIDTH}
                height={HEIGHT}
                borderRadius={'r16'}
                overflow={'hidden'}
                borderWidth={1}
                style={styles.container}
            >
                <BackgroundContainer image={image}>
                    {isLive ? (
                        <Box position={'absolute'} zIndex={1} top={22} left={16}>
                            <LiveTag size={'large'} />
                        </Box>
                    ) : null}
                    <Box
                        zIndex={1}
                        height={HEIGHT}
                        justifyContent={'flex-end'}
                        paddingBottom={'s12'}
                        paddingHorizontal={'s16'}
                    >
                        <Text variant={'titleSmall'} paddingBottom={'s2'}>
                            {title}
                        </Text>
                        <Text variant={'bodySmaller'} color={'neutral300'} lineHeight={16} paddingBottom={'s2'}>
                            {info}
                        </Text>
                        <Text variant={'bodySmaller'} color={'neutral300'} lineHeight={16}>
                            {date !== '' ? date : dateRanges}
                        </Text>
                    </Box>
                </BackgroundContainer>
            </Box>
        </TouchableOpacity>
    );
};

const BackgroundContainer = ({ image, children }: { image?: string; children: ReactNode }) => {
    return (
        <Box zIndex={0}>
            {image ? (
                <Box>
                    <ImageWithGradient
                        source={{ uri: image }}
                        gradientColors={[
                            withOpacity(designSystem.colors.neutral900, 0.4),
                            designSystem.colors.neutral900,
                        ]}
                        gradientStart={{ x: 0, y: 0 }}
                        gradientEnd={{ x: 0, y: 0.5 }}
                    />
                    {children}
                </Box>
            ) : (
                <>
                    <LinearGradient
                        colors={[ds.colorValue.background.neutral.inverse, designSystem.colors.neutralBlack]}
                        start={{ x: 0, y: 0 }}
                        end={{ x: 0, y: 1 }}
                        width={WIDTH}
                        height={HEIGHT}
                    />
                    {children}
                </>
            )}
        </Box>
    );
};

const styles = StyleSheet.create({
    container: {
        borderColor: ds.colorValue.border.subtle,
    },
});
